import React, { useEffect, useState } from "react";
import { Modal, Button, Spin, DatePicker, Select, Row, Col, Avatar, Checkbox, message } from "antd";
import { AddGeneralAssignment, Test, UserTestAssignment } from "../../stores/TestsStore";
import { useTranslation } from "react-i18next";
import { useStore } from "../../stores/root-store-context";
import { GoogleGenerativeAI } from "@google/generative-ai";
import dayjs, { Dayjs } from 'dayjs';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { SelectProps } from "antd/lib";
import { TestHelper } from "../../helpers/testHelper";
import { toJS } from "mobx";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { Roles } from "../../enums/Roles";
import { Employee } from "../../models/Employee";
import { Languages } from "../../enums/Languages";

interface TestUserPlanIndividualGenerator {
  testId: number;
  userId: number;
  startDate: string;
  endDate: string;
}

interface TestGeneralAssignModelProps {
  visible: boolean;
  onClose: () => void;
  allCompanyUsers: Employee[];
  companyAssignments: UserTestAssignment[];
  setCompanyAssignments: (userAssignments: UserTestAssignment[]) => void;
  setAllCompanyUser: (companyUserAll: Employee[]) => void;
  allTests: Test[];
}

export interface TestGenerationRecomendedTest {
  testId: number;
}

export const TestGeneralAssignModal: React.FC<TestGeneralAssignModelProps> = ({
  visible,
  onClose,
  allCompanyUsers,
  companyAssignments,
  setCompanyAssignments,
  allTests,
  setAllCompanyUser
}) => {
  const GEMINI_MODEL = `${process.env.REACT_APP_AI_GEMINI_MODEL}`;
  const API_AI_KEY = `${process.env.REACT_APP_AI_GEMINI_API_KEY}`;
  const { t } = useTranslation();
  const { applicationStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [recommendedTests, setRecommendedTests] = useState<Test[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedRole, setSelectedRole] = useState<number[]>([]);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [selectedTests, setSelectedTests] = useState<Test[]>([]);
  const { testsStore, employeesStore } = useStore();
  const [filteredUsers, setFilteredUsers] = useState<Employee[]>(applicationStore.users);
  const [includedUsers, setIncludedUsers] = useState<number[]>([]);
  const [isIndividualPlan, setIsIndividualPlan] = useState<boolean>(false);
  const [isOverwritePlan, setIsOverwritePlan] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      generationRecommendedTestsForUsers();
      setDefaultDate();
      handleRoleChange([Roles.User]);
    }
  }, [visible]);

  const setDefaultDate = () => {
    const today = dayjs();
    const endOfCurrentYear = dayjs().endOf('year');
    const monthsUntilEndOfYear = endOfCurrentYear.diff(today, 'month');

    if (monthsUntilEndOfYear < 1) {
      setSelectedDate(dayjs().add(1, 'year').endOf('year'));
    } else {
      setSelectedDate(endOfCurrentYear);
    }
  }

  const skillLevelMapping = (level: number) => {
    if (level <= 2) return 'Junior';
    if (level <= 4) return 'Middle';
    return 'Senior';
  };

  const generationPlanTestForAllUser = async () => {

    const transformedAllTests = allTests.map((test) => ({
      id: test.id,
      name: test.name,
      levelName: test.level,
      skillName: applicationStore.skills.find((s) => s.skillId == test.skillId?.toString())?.title,
    }));

    const filteredUsers = allCompanyUsers
      .filter((user) => includedUsers.includes(Number(user.id)))
      .filter((user) => user.roles.some(role => selectedRole.includes(role.id)));

    const usersWithSkills = filteredUsers
      .map((user) => {
        const userSkills = user.userSkills.map((skill) => ({
          name: skill.skill.title,
          levelName: skillLevelMapping(Number(skill.skillLevel)),
          isConfirmed: skill.isConfirmed ?? false,
        }));

        return {
          id: user.id,
          skills: userSkills,
        };
      });

    const existAssignments = companyAssignments
      .filter((assignment) => includedUsers.includes(assignment.assignedToUserId))
      .map((assignment) => {
        return { testId: assignment.testId };
      });

    const jsonTransformedUsersWithSkills = JSON.stringify(usersWithSkills, null, 2);
    const jsonTransformedAllTests = JSON.stringify(transformedAllTests, null, 2);
    const jsonTransformedAllAssignments = JSON.stringify(existAssignments, null, 2);

    const requestAI = `
        Based on the following data about company users, their skills, levels, and skill confirmation status, as well as the list of all available tests and existing test assignments, generate an individual structured test plan for each user.

        ### Plan Requirements:
        - The test plan should cover the next 12 months, starting from the first day of the next month.
        - Each user should receive exactly 4 tests, scheduled at 3-month intervals (March, June, September, December).
        - The selected tests should be the most suitable for each user, considering their specific skills and levels.
        - Tests should be chosen based on the following criteria:
          - If multiple tests exist for a skill, prefer the one closest to the user's level, but prioritize higher-level tests for skill improvement.
          - The selected tests should cover different skill areas to ensure a well-rounded assessment for each user.
          - The same test should not be assigned more than once within the 12-month period.
          - The tests should align with the user’s current skills and levels.
        - Avoid assigning tests that the user has already completed if such data is available.

        ### Input Data:
        Current date: ${new Date().toISOString()}

        Users and their skills:
        ${jsonTransformedUsersWithSkills}

        All available tests:
        ${jsonTransformedAllTests}

        Existing test assignments:
        ${jsonTransformedAllAssignments}

        Please generate a test plan where each user is assigned 4 tests over the next 12 months. 
        The response must be a JSON array of objects with the following structure:
        [
            {
                "testId": Test ID,
                "userId": User Id,
                "startDate": Start date,
                "endDate": End date
            }
        ]
        No additional information.
    `;

    try {
      const genAI = new GoogleGenerativeAI(API_AI_KEY);
      const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

      const resultResponse = await model.generateContent(requestAI);

      const rawResponse = resultResponse.response.text().trim();
      const jsonStart = rawResponse.indexOf("[");
      const jsonEnd = rawResponse.lastIndexOf("]") + 1;

      if (jsonStart === -1 || jsonEnd === 0) {
        throw new Error("Response does not contain a valid JSON array.");
      }

      const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

      const result: TestUserPlanIndividualGenerator[] = JSON.parse(cleanedResponse);
      console.log("Result", result);

      return result;
    } catch (error) {
      console.error("Error fetching data from API:", error);
      return [];
    }
  };

  const generationTestPlanForUsers = async () => {
    const planTestArray = await generationPlanTestForAllUser();

    const testPlanAssignmentArray: UserTestAssignment[] = planTestArray.map((item) => {
      const existTest = allTests.find(t => t.id == item.testId);

      return {
        id: 0,
        test: null,
        testId: item.testId,
        language: existTest?.language === "all" ? Languages.ANY : existTest?.language ?? null,
        numberOfAttempts: existTest?.numberOfAttempts ?? 2,
        finishAssignedDate: item.endDate,
        startAssignedDate: item.startDate,
        assignedDate: new Date().toISOString(),
        assignmentStatusId: 1,
        assignedToUser: null,
        assignmentStatus: null,
        assignedToUserId: item.userId,
        assignedByUserId: Number(applicationStore.user.id),
        testAttempts: []
      };
    });

    const userTestPlanMap = testPlanAssignmentArray.reduce<Map<number, UserTestAssignment[]>>((acc, assignment) => {
      if (!acc.has(assignment.assignedToUserId)) {
        acc.set(assignment.assignedToUserId, []);
      }
      acc.get(assignment.assignedToUserId)?.push(assignment);
      return acc;
    }, new Map());

    console.log("userTestPlanMap", userTestPlanMap);
    return userTestPlanMap;
  };

  const generationRecommendedTestsForUsers = async () => {
    const transformedAllTests = allTests.map((test) => ({
      id: test.id,
      name: test.name,
      levelName: test.level,
      skillName: applicationStore.skills.find((s) => s.skillId == test.skillId?.toString())?.title,
    }));

    const usersWithSkills = allCompanyUsers
      .map((user) => {
        const userSkills = user.userSkills.map((skill) => ({
          name: skill.skill.title,
          levelName: skillLevelMapping(Number(skill.skillLevel)),
          isConfirmed: skill.isConfirmed ?? false,
        }));

        return {
          id: user.id,
          skills: userSkills,
        };
      });

    const existAssignments = companyAssignments
      .map((assignment) => {
        return { testId: assignment.testId };
      });

    const jsonTransformedUserWithSkills = JSON.stringify(usersWithSkills, null, 2);
    const jsonTransformedAllTests = JSON.stringify(transformedAllTests, null, 2);
    const jsonTransformedAllAssignments = JSON.stringify(existAssignments, null, 2);

    const requestAI = `
      You are an assistant that helps recommend tests for users. 
      Your task is to select up to 5 tests that are suitable for all users in the company.

      ### Conditions:
      1. The tests must be for skills that are **not confirmed** (isConfirmed = false) for all users.
      2. If no such skills exist, suggest tests for **related skills** from the same domain.
      3. The tests **must not match** any already assigned tests (existAssignments).
      4. Return the result as a JSON array of objects with the following fields:
        - testId (Test ID)

      ### Data:
      - **Users and their skills:**  
      ${jsonTransformedUserWithSkills}

      - **All available tests:**  
      ${jsonTransformedAllTests}

      - **Assigned tests:**  
      ${jsonTransformedAllAssignments}

      ### Response format:
      [
        {
          "testId": "123",
        },
        {
          "testId": "456",
        }
      ]
    `;

    try {
      setLoading(true);
      const genAI = new GoogleGenerativeAI(API_AI_KEY);
      const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

      const resultResponse = await model.generateContent(requestAI);

      const rawResponse = resultResponse.response.text().trim();
      const jsonStart = rawResponse.indexOf("[");
      const jsonEnd = rawResponse.lastIndexOf("]") + 1;

      if (jsonStart === -1 || jsonEnd === 0) {
        throw new Error("Response does not contain a valid JSON array.");
      }

      const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

      const result: TestGenerationRecomendedTest[] = JSON.parse(cleanedResponse);

      const recommendedTestsData = allTests.filter((test) =>
        result.some((r) => r.testId === test.id)
      );

      setRecommendedTests(recommendedTestsData);
      setSelectedTests(recommendedTestsData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const addTest = (test: Test) => {
    if (!selectedTests.some((t) => t.id === test.id)) {
      setSelectedTests([...selectedTests, test]);
    }
  };

  const removeTest = (testId: number) => {
    setSelectedTests(selectedTests.filter((test) => test.id != testId));
  };

  const handleRoleChange = (values: number[]) => {
    setSelectedRole(values);

    const filtered = applicationStore.users.filter(
      (user) =>
        user.isActive && user.isApproveInvite &&
        values.some((role) => user.roles.some((userRole) => userRole.id == role))
    );    
    setFilteredUsers(filtered);
  };

  const handleIncludedUsersChange = (values: number[]) => {
    setIncludedUsers(values);
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    setSelectedRole([Roles.User]);
    setSelectedTests([]);
    setFilteredUsers([]);
    setIncludedUsers([]);
    setIsIndividualPlan(false);
    setSelectedDate(null);

    onClose();
  };

  const handleAssignModal = async () => {
    if (!isIndividualPlan) {
      if (!selectedDate) {
        message.error(t("tests.ErrorSelectDeadlineDate"));
        return;
      }

      if (selectedRole.length === 0) {
        message.error(t("tests.ErrorSelectAtLeastOneRole"));
        return;
      }

      if (includedUsers.length === 0) {
        message.error(t("tests.ErrorSelectAtLeastOneUser"));
        return;
      }

      if (selectedTests.length === 0) {
        message.error(t("tests.ErrorSelectAtLeastOneTest"));
        return;
      }
    } else {
      if (selectedRole.length === 0) {
        message.error(t("tests.ErrorSelectAtLeastOneRole"));
        return;
      }

      if (includedUsers.length === 0) {
        message.error(t("tests.ErrorSelectAtLeastOneUser"));
        return;
      }
    }

    if (selectedRole.length > 0) {
      setLoading(true);

      if (isIndividualPlan) {
        setLoading(true);
      
        const generationPlansForUsers = await generationTestPlanForUsers();
      
        try {
          const promises = [];
      
          for (const [userId, testAssignments] of generationPlansForUsers) {
            const promise = testsStore.addAssignmentsPlan(testAssignments, userId, isOverwritePlan)
              .then(async (result) => {
                if (result) {
                  const updatedUsers = allCompanyUsers.map(user =>
                    Number(user.id) === userId ? { ...user, testPlan: result.userTestPlans, isGenerationUserTestPlan: true } : user
                  );
      
                  const companyAssignments = await testsStore.getAllAssignmentByCompanyId(applicationStore.company.Id ?? "");
      
                  setCompanyAssignments(companyAssignments);
                  setAllCompanyUser(updatedUsers);
                  return true;
                } else {
                  message.error(`${t("Error")} (User ID: ${userId})`);
                  return false;
                }
              })
              .catch((error) => {
                console.error(error);
                message.error(`${t("Error")} (User ID: ${userId})`);
                return false;
              });
      
            promises.push(promise);
          }
      
          const results = await Promise.all(promises);
      
          const allSuccess = results.every((res) => res === true);
      
          if (allSuccess) {
            message.success(t("Success"));
          } else {
            message.error(t("tests.errorSomeAssignments"));
          }
      
        } catch (e) {
          console.error(e);
          message.error(t("Error"));
        } finally {
          setLoading(false);
          handleCloseModal();
        }
      }      
      else {
        const newGeneralAssignment: AddGeneralAssignment = {
          assignedByUserId: Number(applicationStore.user.id ?? 0),
          companyId: Number(applicationStore.company.Id ?? 0),
          includedUsers: includedUsers,
          roleArray: selectedRole,
          tests: selectedTests,
          finishAssignedDate: selectedDate == null ? null : selectedDate.toISOString()
        }

        try {
          const result = await testsStore.addGeneralAssignments(newGeneralAssignment);

          if (result) {
            message.success(t("Success"));

            const companyAssignments = await testsStore.getAllAssignmentByCompanyId(applicationStore.company.Id ?? "");
            setCompanyAssignments(companyAssignments);

            await employeesStore.getUsers().then(res => {
              setAllCompanyUser(toJS(employeesStore.users).filter((u: Employee) => u.isActive));
            });

            setLoading(false);
          }
          else {
            message.error(t("Error"));
          }
        }
        catch (error) {
          message.error(t("Error"));
          setLoading(false);
        }
        finally {
          handleCloseModal();
        }
      }
    }
  }


  const rolesOptions = Object.entries(Roles)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: t(key),
      value: value,
    }));

  const userOptions: SelectProps['options'] = filteredUsers.map((user) => ({
    label: `${user.firstName} ${user.lastName}`,
    value: user.id,
  }));

  const handleIndividualPlanChange = (e: CheckboxChangeEvent) => {
    setIsIndividualPlan(e.target.checked);
  };

  const handleIsOverwritePlanChange = (e: CheckboxChangeEvent) => {
    setIsOverwritePlan(e.target.checked);
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCloseModal}
      footer={
        loading ? [] : [
          <Button key="cancel" onClick={handleCloseModal}>
            {t("Cancel")}
          </Button>,
          <Button key="submit" type="primary" onClick={handleAssignModal}>
            {t("tests.assign")}
          </Button>,
        ]
      }

    >
      {loading ? (
        <div className="text-center p-5">
          <Spin size="large" />
          <p>{t(`tests.generation`)}</p>
        </div>
      ) : (
        <div>
          {!isIndividualPlan && (
            <div>
              <p style={{ fontWeight: "bold", marginBottom: "8px" }}>{t("tests.selectDeadlineDate")}</p>
              <DatePicker
                style={{ width: "100%", marginBottom: "16px" }}
                value={selectedDate}
                required
                onChange={(value) => setSelectedDate(value)}
                disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
              />
            </div>
          )}

          <p style={{ fontWeight: "bold", marginBottom: "8px" }}>{t("CompanyRole")}</p>
          <div>
            <Select
              mode="multiple"
              style={{ width: '100%', marginBottom: '10px' }}
              placeholder={t('select_role')}
              onChange={handleRoleChange}
              options={rolesOptions}
              value={selectedRole}
            />
          </div>

          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('tests.chooseUsers')}</p>
          <div>
            <Select
              mode="multiple"
              style={{ width: '100%', marginBottom: '10px' }}
              placeholder={t('tests.select_users_to_add_test')}
              onChange={handleIncludedUsersChange}
              value={includedUsers}
              disabled={filteredUsers.length === 0}
              filterOption={(input, option) => {
                if (option && option.label) {
                  const userEmail = String(option.label).toLowerCase();
                  const searchText = input.toLowerCase();
                  return userEmail.includes(searchText);
                }
                return false;
              }}
              optionLabelProp="label"
            >
              {filteredUsers.map((user) => (
                <Select.Option key={user.id} value={user.id} label={user.email}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <img
                      src={user.photoId
                        ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${user.photoId}`
                        : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`}
                      alt="avatar"
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {user.firstName} {user.lastName}
                      </div>
                      <span>{user.email}</span>
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 10, marginBottom: 15 }}>
            <Checkbox
              value={isIndividualPlan}
              checked={isIndividualPlan}
              onChange={handleIndividualPlanChange}
            >
              <strong>{t("tests.generateIndividualPlan")}</strong>
            </Checkbox>

            {isIndividualPlan && (
              <Checkbox
                value={isOverwritePlan}
                checked={isOverwritePlan}
                onChange={handleIsOverwritePlanChange}
              >
                <strong>{t("tests.overwriteExistPlan")}</strong>
              </Checkbox>
            )}
          </div>

          {!isIndividualPlan && (
            <div>
              <p style={{ marginBottom: "8px" }}>{t("tests.selectedTest")}</p>
              <div style={{ maxHeight: "200px", overflowY: "auto", padding: "8px", border: "1px solid #ddd", borderRadius: "5px" }}>
                {selectedTests.map((test) => (
                  <div key={test.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px", borderBottom: "1px solid #eee" }}>
                    <div>
                      <span style={{ fontWeight: "bold" }}>{applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"}</span>
                      <span style={{ fontSize: "12px", color: "#777", marginLeft: "8px" }}>{t("add_test.Level")}: {test.level}</span>
                    </div>
                    <Button type="link" icon={<DeleteOutlined />} danger onClick={() => removeTest(test.id)} />
                  </div>
                ))}
              </div>

              <p style={{ marginTop: "16px" }}>{t("add_test.addTest")}</p>
              <Select
                style={{ width: "100%" }}
                showSearch
                value={selectedTest ? selectedTest.id : undefined}
                onChange={(value) => setSelectedTest(allTests.find((test) => test.id === value) || null)}
                filterOption={(input, option) => {
                  const test = allTests.find((t) => t.id == option?.value);
                  if (!test) return false;

                  const skillTitle = applicationStore.skills.find((s) => s.skillId == test.skillId?.toString())?.title || "";
                  return skillTitle.toLowerCase().includes(input.toLowerCase());
                }}
                optionLabelProp="label"
              >
                {allTests.map((test) => {
                  const skillTitle =
                    applicationStore.skills.find((s) => s.skillId == test.skillId?.toString())?.title || "unknown_skill";

                  return (
                    <Select.Option key={test.id} value={test.id} label={`${applicationStore.skills.find(s => s.skillId == test.skillId?.toString())?.title || "unknown_skill"} - ${test.level}`}>
                      <Row align="middle">
                        <Col>
                          <Avatar
                            src={TestHelper.getImageSkill(skillTitle)}
                            alt="test-image"
                            size="large"
                          />
                        </Col>
                        <Col style={{ marginLeft: "10px" }}>
                          <strong>{skillTitle}</strong>
                          <div style={{ color: "#777" }}>{test.level}</div>
                        </Col>
                      </Row>
                    </Select.Option>
                  );
                })}
              </Select>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (selectedTest) {
                    addTest(selectedTest);
                    setSelectedTest(null);
                  }
                }}
                style={{ marginTop: "8px", marginBottom: "20px", width: "100%" }}
              >
                {t("tests.addSelectedTest")}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
